/* =====================================================================
   Career.jsx — ASSOCHAM "Careers" page (React)
   Theme: #DA2128 · #E68F00 · #FAF7F2   Buttons: #DC2928 → #E65228
   API endpoints → https://www.assocham.org
   ===================================================================== */

import React, { useCallback, useEffect, useRef, useState } from "react";

/* ---------------------------------------------------------------------
   CONFIG — ASSOCHAM endpoints
--------------------------------------------------------------------- */
const API_BASE = "https://www.assocham.org/assocham_backend";
const CAREER_ENDPOINT     = `${API_BASE}/career.php`;
const INTERNSHIP_ENDPOINT = `${API_BASE}/internship.php`;

const RECAPTCHA_SITE_KEY = "6LcCYwglAAAAAK7MWiTOO9H2Urm1IZsmHQD4hBeo";
const HR_EMAIL = "hr@assocham.com";
const MAX_RESUME_MB = 5;

/* ---------------------------------------------------------------------
   reCAPTCHA loader
--------------------------------------------------------------------- */
const GLOBAL_CB = "__careerRecaptchaCallbacks";
let recaptchaLoader = null;

function loadRecaptcha() {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
    return Promise.resolve(window.grecaptcha);
  }
  if (recaptchaLoader) return recaptchaLoader;

  recaptchaLoader = new Promise((resolve, reject) => {
    window[GLOBAL_CB] = window[GLOBAL_CB] || [];
    window[GLOBAL_CB].push(resolve);

    if (!document.getElementById("career-recaptcha-script")) {
      window.__careerRecaptchaOnLoad = function () {
        const cbs = window[GLOBAL_CB] || [];
        window[GLOBAL_CB] = [];
        cbs.forEach((cb) => cb(window.grecaptcha));
      };
      const s = document.createElement("script");
      s.id = "career-recaptcha-script";
      s.src =
        "https://www.google.com/recaptcha/api.js?onload=__careerRecaptchaOnLoad&render=explicit";
      s.async = true;
      s.defer = true;
      s.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
      document.body.appendChild(s);
    }
  });

  return recaptchaLoader;
}

/* ---------------------------------------------------------------------
   <Recaptcha />
--------------------------------------------------------------------- */
function Recaptcha({ siteKey, onVerify }) {
  const containerRef = useRef(null);
  const onVerifyRef = useRef(onVerify);

  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  useEffect(() => {
    let cancelled = false;
    loadRecaptcha()
      .then((grecaptcha) => {
        if (cancelled || !containerRef.current) return;
        grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerifyRef.current && onVerifyRef.current(token),
          "expired-callback": () => onVerifyRef.current && onVerifyRef.current(""),
          "error-callback": () => onVerifyRef.current && onVerifyRef.current(""),
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [siteKey]);

  return <div className="career-recaptcha" ref={containerRef} />;
}

/* ---------------------------------------------------------------------
   Validation helpers
--------------------------------------------------------------------- */
const EMAIL_RE = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
const PHONE_RE = /^[0-9+\-\s()]{8,20}$/;

const INITIAL_CAREER = { name: "", phone: "", email: "", message: "" };
const INITIAL_INTERN = {
  applicant_name: "",
  applicant_contact: "",
  applicant_email: "",
  applicant_location: "",
  short_note: "",
};

function validateCareer(values, token) {
  const e = {};
  if (!values.name.trim()) e.name = "Please enter your name.";
  if (!values.phone.trim()) e.phone = "Please enter your contact number.";
  else if (!PHONE_RE.test(values.phone.trim()))
    e.phone = "Enter a valid contact number (8–20 digits).";
  if (!values.email.trim()) e.email = "Please enter your email id.";
  else if (!EMAIL_RE.test(values.email.trim()))
    e.email = "Please enter valid email address!";
  if (!token) e.career_hiddenRecaptcha = "Please verify that you are not a robot.";
  return e;
}

function validateIntern(values, token, file) {
  const e = {};
  if (!values.applicant_name.trim()) e.applicant_name = "Please enter your name.";
  if (!values.applicant_contact.trim())
    e.applicant_contact = "Please enter your contact number.";
  else if (!PHONE_RE.test(values.applicant_contact.trim()))
    e.applicant_contact = "Enter a valid contact number (8–20 digits).";
  if (!values.applicant_email.trim()) e.applicant_email = "Please enter your email id.";
  else if (!EMAIL_RE.test(values.applicant_email.trim()))
    e.applicant_email = "Please enter valid email address!";
  if (!values.applicant_location.trim())
    e.applicant_location = "Please enter your location.";
  if (!file) e.applicant_resume = "Please upload your resume.";
  else if (file.size > MAX_RESUME_MB * 1024 * 1024)
    e.applicant_resume = `File too large. Max ${MAX_RESUME_MB} MB allowed.`;
  if (!token) e.intern_hiddenRecaptcha = "Please verify that you are not a robot.";
  return e;
}

/* ---------------------------------------------------------------------
   API helpers
--------------------------------------------------------------------- */
async function postCareerToAssocham({ form, token }) {
  const fd = new FormData();
  fd.append("name", form.name.trim());
  fd.append("phone", form.phone.trim());
  fd.append("email", form.email.trim());
  fd.append("message", form.message.trim());
  fd.append("g-recaptcha-response", token);
  fd.append("submit_career", "1");

  const res = await fetch(CAREER_ENDPOINT, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Server responded ${res.status}`);
  return res.text();
}

async function postInternshipToAssocham({ form, token, resumeFile }) {
  const fd = new FormData();
  fd.append("applicant_name", form.applicant_name.trim());
  fd.append("applicant_contact", form.applicant_contact.trim());
  fd.append("applicant_email", form.applicant_email.trim());
  fd.append("applicant_location", form.applicant_location.trim());
  fd.append("short_note", form.short_note.trim());
  fd.append("g-recaptcha-response", token);
  fd.append("apply_send", "1");
  if (resumeFile) fd.append("applicant_resume", resumeFile, resumeFile.name);

  const res = await fetch(INTERNSHIP_ENDPOINT, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Server responded ${res.status}`);
  return res.text();
}

/* =====================================================================
   MAIN COMPONENT
   ===================================================================== */
export default function Career() {
  const [careerForm, setCareerForm] = useState(INITIAL_CAREER);
  const [careerErrors, setCareerErrors] = useState({});
  const [careerToken, setCareerToken] = useState("");
  const [careerResetKey, setCareerResetKey] = useState(0);
  const [careerStatus, setCareerStatus] = useState(null);
  const [careerSubmitting, setCareerSubmitting] = useState(false);

  const [internForm, setInternForm] = useState(INITIAL_INTERN);
  const [internErrors, setInternErrors] = useState({});
  const [internToken, setInternToken] = useState("");
  const [internResetKey, setInternResetKey] = useState(0);
  const [internStatus, setInternStatus] = useState(null);
  const [internSubmitting, setInternSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (ev) => ev.key === "Escape" && setModalOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [modalOpen]);

  const handleCareerChange = useCallback((ev) => {
    const { name, value } = ev.target;
    setCareerForm((p) => ({ ...p, [name]: value }));
    setCareerErrors((p) => (p[name] ? { ...p, [name]: undefined } : p));
  }, []);

  const handleInternChange = useCallback((ev) => {
    const { name, value } = ev.target;
    setInternForm((p) => ({ ...p, [name]: value }));
    setInternErrors((p) => (p[name] ? { ...p, [name]: undefined } : p));
  }, []);

  const handleResumeChange = useCallback((ev) => {
    const file = ev.target.files && ev.target.files[0];
    setResumeFile(file || null);
    setInternErrors((p) => (p.applicant_resume ? { ...p, applicant_resume: undefined } : p));
  }, []);

  const handleCareerSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validateCareer(careerForm, careerToken);
    setCareerErrors(errs);
    if (Object.keys(errs).length) return;

    setCareerSubmitting(true);
    setCareerStatus(null);
    try {
      await postCareerToAssocham({ form: careerForm, token: careerToken });
      setCareerStatus({
        type: "success",
        msg: "Your profile has been submitted successfully.",
      });
      setCareerForm(INITIAL_CAREER);
      setCareerToken("");
      setCareerResetKey((k) => k + 1);
    } catch (err) {
      setCareerStatus({
        type: "error",
        msg: err && err.message
          ? `Submission failed: ${err.message}`
          : "OOPS .. Something went wrong.",
      });
    } finally {
      setCareerSubmitting(false);
    }
  };

  const handleInternSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validateIntern(internForm, internToken, resumeFile);
    setInternErrors(errs);
    if (Object.keys(errs).length) return;

    setInternSubmitting(true);
    setInternStatus(null);
    try {
      await postInternshipToAssocham({
        form: internForm,
        token: internToken,
        resumeFile,
      });
      setInternStatus({
        type: "success",
        msg: "Your application has been submitted successfully.",
      });
      setInternForm(INITIAL_INTERN);
      setResumeFile(null);
      setInternToken("");
      setInternResetKey((k) => k + 1);
      const fileInput = document.getElementById("careerResumeInput");
      if (fileInput) fileInput.value = "";
      setModalOpen(false);
    } catch (err) {
      setInternStatus({
        type: "error",
        msg: err && err.message
          ? `Submission failed: ${err.message}`
          : "OOPS .. Something went wrong.",
      });
    } finally {
      setInternSubmitting(false);
    }
  };

  /* ===================== RENDER ===================== */
  return (
    <div className="career-page">
      <style>{careerCss}</style>

      {/* ================= HERO ================= */}
      <section className="career-hero">
        <div className="career-hero__inner">
          <div className="career-hero__copy">
            <span className="career-hero__eyebrow">Careers @ ASSOCHAM</span>
            <h1 className="career-hero__title">Careers</h1>
            <span className="career-hero__rule" />
            <p className="career-hero__tagline">
              ASSOCHAM: The Knowledge <br /> Architect Of India
            </p>
            <p className="career-hero__lead">
              Be part of a team that shapes policy, empowers industry and drives
              India&rsquo;s socio‑economic growth story.
            </p>
            {/* <div className="career-hero__actions">
              <a className="career-btn career-btn--primary" href="#career-form">
                Submit Your Profile
              </a>
              <a className="career-btn career-btn--ghost" href="#internship">
                Apply for Internship
              </a>
            </div> */}
          </div>
{/* 
          <div className="career-hero__panel">
            <div className="career-glass">
              <h3>Where you can make an impact</h3>
              <ul>
                <li>Policy Advocacy</li>
                <li>Industry Representation</li>
                <li>Research &amp; Knowledge</li>
                <li>Global Collaborations</li>
                <li>Events &amp; Partnerships</li>
              </ul>
            </div>
          </div> */}
        </div>
      </section>

      {/* ================= INTRO ================= */}
      <section className="career-section">
        <div className="career-intro">
          <p>
            The Associated Chambers of Commerce and Industry of India (ASSOCHAM)
            believes that our success is intrinsically linked to the talent and
            aspirations of our team. We invite dynamic individuals to be part of
            our journey and foster an inclusive work culture that values unique
            perspectives, encourages collaboration, and empowers individuals to
            unleash their full potential.
          </p>
          <p>
            ASSOCHAM&rsquo;s expansive footprint and global alliances offer a
            unique stage to engage with diverse industries and influencers,
            fostering dialogue with varied stakeholders.
          </p>
          <p>
            Elevate your career with us, where opportunities await in Policy
            Advocacy, Industry Representation, Research and Global
            collaborations. We invite passionate individuals to share their
            expertise and contribute to the success of the chamber.{" "}
            <strong>
              To be part of the growth journey, share your CV with us at{" "}
              <a href={`mailto:${HR_EMAIL}`}>{HR_EMAIL}</a>.
            </strong>
          </p>
        </div>
      </section>

      {/* ================= CAREER FORM ================= */}
      {/* <section className="career-section career-section--tight" id="career-form">
        <div className="career-section__head">
          <span className="career-chip">Join the team</span>
          <h2>Submit Your Profile</h2>
          <p>Fill in your details and our HR team will get in touch with you.</p>
        </div>

        <div className="career-card">
          {careerStatus && (
            <div className={`career-alert career-alert--${careerStatus.type}`} role="status">
              {careerStatus.msg}
            </div>
          )}

          <form onSubmit={handleCareerSubmit} noValidate>
            <div className="career-grid">
              <div className="career-field">
                <label htmlFor="careerName">
                  Name <b>*</b>
                </label>
                <input
                  id="careerName"
                  type="text"
                  name="name"
                  className={`career-input ${careerErrors.name ? "is-invalid" : ""}`}
                  placeholder="Your full name"
                  value={careerForm.name}
                  onChange={handleCareerChange}
                />
                {careerErrors.name && <p className="career-error">{careerErrors.name}</p>}
              </div>

              <div className="career-field">
                <label htmlFor="careerPhone">
                  Contact No. <b>*</b>
                </label>
                <input
                  id="careerPhone"
                  type="tel"
                  name="phone"
                  className={`career-input ${careerErrors.phone ? "is-invalid" : ""}`}
                  placeholder="+91 98xxxxxxxx"
                  value={careerForm.phone}
                  onChange={handleCareerChange}
                />
                {careerErrors.phone && <p className="career-error">{careerErrors.phone}</p>}
              </div>

              <div className="career-field career-field--full">
                <label htmlFor="careerEmail">
                  Email id <b>*</b>
                </label>
                <input
                  id="careerEmail"
                  type="email"
                  name="email"
                  className={`career-input ${careerErrors.email ? "is-invalid" : ""}`}
                  placeholder="you@example.com"
                  value={careerForm.email}
                  onChange={handleCareerChange}
                />
                {careerErrors.email && <p className="career-error">{careerErrors.email}</p>}
              </div>

              <div className="career-field career-field--full">
                <label htmlFor="careerMessage">Cover Note</label>
                <textarea
                  id="careerMessage"
                  name="message"
                  rows={4}
                  className="career-textarea"
                  placeholder="Tell us briefly about yourself…"
                  value={careerForm.message}
                  onChange={handleCareerChange}
                />
              </div>

              <div className="career-field career-field--full">
                <Recaptcha
                  key={careerResetKey}
                  siteKey={RECAPTCHA_SITE_KEY}
                  onVerify={setCareerToken}
                />
                {careerErrors.career_hiddenRecaptcha && (
                  <p className="career-error">{careerErrors.career_hiddenRecaptcha}</p>
                )}
              </div>
            </div>

            <div className="career-actions">
              <button
                type="submit"
                className="career-btn career-btn--primary"
                disabled={careerSubmitting}
              >
                {careerSubmitting ? "Submitting…" : "Submit"}
              </button>
              <span className="career-hint">
                Prefer email? Write to{" "}
                <a href={`mailto:${HR_EMAIL}`}>{HR_EMAIL}</a>
              </span>
            </div>
          </form>
        </div>
      </section> */}

      {/* ================= INTERNSHIP ================= */}
      {/* <section className="career-section" id="internship">
        <div className="career-intern">
          <div className="career-intern__art">
            <span className="career-intern__badge">Internship Program</span>
          </div>
          <div className="career-intern__body">
            <h2>Apply for Internship</h2>
            <span className="career-rule" />
            <p>
              We are looking for people with a variety of academic backgrounds
              from colleges and universities, who are passionate about
              India&rsquo;s socio‑economic growth story. Join our internship
              program to put your talents and knowledge to work.
            </p>
            <button
              type="button"
              className="career-btn career-btn--primary"
              onClick={() => setModalOpen(true)}
            >
              <span aria-hidden="true">💼</span> Apply Now
            </button>
          </div>
        </div>

        {internStatus && (
          <div
            className={`career-alert career-alert--${internStatus.type} career-alert--floating`}
            role="status"
          >
            {internStatus.msg}
          </div>
        )}
      </section> */}

      {/* ================= MODAL ================= */}
      {modalOpen && (
        <div
          className="career-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setModalOpen(false);
          }}
        >
          <div className="career-modal" role="dialog" aria-modal="true" aria-label="Apply Now">
            <button
              type="button"
              className="career-modal__close"
              onClick={() => setModalOpen(false)}
              aria-label="Close"
            >
              &times;
            </button>

            <h4 className="career-modal__title">Apply Now</h4>
            <p className="career-modal__sub">
              Share a few details and upload your resume — it takes a minute.
            </p>

            <form onSubmit={handleInternSubmit} noValidate>
              <div className="career-grid">
                <div className="career-field">
                  <label htmlFor="applicant_name">
                    Name <b>*</b>
                  </label>
                  <input
                    id="applicant_name"
                    type="text"
                    name="applicant_name"
                    className={`career-input ${internErrors.applicant_name ? "is-invalid" : ""}`}
                    value={internForm.applicant_name}
                    onChange={handleInternChange}
                  />
                  {internErrors.applicant_name && (
                    <p className="career-error">{internErrors.applicant_name}</p>
                  )}
                </div>

                <div className="career-field">
                  <label htmlFor="applicant_contact">
                    Contact No. <b>*</b>
                  </label>
                  <input
                    id="applicant_contact"
                    type="tel"
                    name="applicant_contact"
                    className={`career-input ${internErrors.applicant_contact ? "is-invalid" : ""}`}
                    value={internForm.applicant_contact}
                    onChange={handleInternChange}
                  />
                  {internErrors.applicant_contact && (
                    <p className="career-error">{internErrors.applicant_contact}</p>
                  )}
                </div>

                <div className="career-field">
                  <label htmlFor="applicant_email">
                    Email id <b>*</b>
                  </label>
                  <input
                    id="applicant_email"
                    type="email"
                    name="applicant_email"
                    className={`career-input ${internErrors.applicant_email ? "is-invalid" : ""}`}
                    value={internForm.applicant_email}
                    onChange={handleInternChange}
                  />
                  {internErrors.applicant_email && (
                    <p className="career-error">{internErrors.applicant_email}</p>
                  )}
                </div>

                <div className="career-field">
                  <label htmlFor="applicant_location">
                    Location <b>*</b>
                  </label>
                  <input
                    id="applicant_location"
                    type="text"
                    name="applicant_location"
                    className={`career-input ${internErrors.applicant_location ? "is-invalid" : ""}`}
                    value={internForm.applicant_location}
                    onChange={handleInternChange}
                  />
                  {internErrors.applicant_location && (
                    <p className="career-error">{internErrors.applicant_location}</p>
                  )}
                </div>

                <div className="career-field career-field--full">
                  <label htmlFor="short_note">
                    How would you add value to ASSOCHAM? (100 words or less)
                  </label>
                  <textarea
                    id="short_note"
                    name="short_note"
                    rows={4}
                    maxLength={600}
                    className="career-textarea"
                    value={internForm.short_note}
                    onChange={handleInternChange}
                  />
                </div>

                <div className="career-field career-field--full">
                  <label htmlFor="careerResumeInput">
                    Upload your resume <b>*</b>
                  </label>
                  <input
                    id="careerResumeInput"
                    type="file"
                    name="applicant_resume"
                    accept=".pdf,.doc,.docx"
                    className={`career-file ${internErrors.applicant_resume ? "is-invalid" : ""}`}
                    onChange={handleResumeChange}
                  />
                  <span className="career-hint">
                    PDF / DOC / DOCX · max {MAX_RESUME_MB} MB
                  </span>
                  {internErrors.applicant_resume && (
                    <p className="career-error">{internErrors.applicant_resume}</p>
                  )}
                </div>

                <div className="career-field career-field--full">
                  <Recaptcha
                    key={internResetKey}
                    siteKey={RECAPTCHA_SITE_KEY}
                    onVerify={setInternToken}
                  />
                  {internErrors.intern_hiddenRecaptcha && (
                    <p className="career-error">{internErrors.intern_hiddenRecaptcha}</p>
                  )}
                </div>
              </div>

              <div className="career-actions">
                <button
                  type="submit"
                  className="career-btn career-btn--primary"
                  disabled={internSubmitting}
                >
                  {internSubmitting ? "Submitting…" : "Submit"}
                </button>
                <button
                  type="button"
                  className="career-btn career-btn--muted"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* =====================================================================
   STYLES — ASSOCHAM brand theme
   Primary Red  #DA2128  ·  Gold  #E68F00  ·  Cream  #FAF7F2
   Buttons       #DC2928 → #E65228
   ===================================================================== */
const careerCss = `
.career-page{
  --cp-red:#DA2128;
  --cp-red-dark:#B9181F;
  --cp-red-soft:#FDECEC;
  --cp-gold:#E68F00;
  --cp-gold-dark:#C57A00;
  --cp-gold-soft:#FFF4DE;
  --cp-cream:#FAF7F2;
  --cp-cream-deep:#F1EAE0;
  --cp-text:#2A1A1A;
  --cp-muted:#7A6A63;
  --cp-white:#FFFFFF;
  --cp-border:#EADFD3;
  --cp-danger:#DA2128;
  --cp-success:#1E8E5A;
  --cp-radius:18px;
  --cp-shadow:0 14px 40px rgba(180,40,40,.10);
  --cp-shadow-sm:0 6px 18px rgba(180,40,40,.07);
  --cp-btn-grad:linear-gradient(135deg,#DC2928 0%, #E65228 100%);
  --cp-btn-grad-hover:linear-gradient(135deg,#C41F1F 0%, #D84418 100%);
  font-family:'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif;
  color:var(--cp-text);
  background:var(--cp-cream);
  line-height:1.65;
  -webkit-font-smoothing:antialiased;
}
.career-page *,
.career-page *::before,
.career-page *::after{ box-sizing:border-box; }
.career-page p{ margin:0 0 14px; }
// .career-page a{ color:var(--cp-red); }

/* ================= HERO ================= */
.career-hero{
  position:relative;
  overflow:hidden;
  padding:76px 22px 96px;
  color:#fff;
  background:
    radial-gradient(900px 420px at 88% -10%, rgba(230,143,0,.42), transparent 62%),
    radial-gradient(700px 400px at 5% 110%, rgba(218,33,40,.55), transparent 60%),
    linear-gradient(135deg,#8E0F14 0%, #B9181F 45%, #DA2128 100%);
}
.career-hero::after{
  content:"";
  position:absolute; inset:0;
  background-image:
    linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px);
  background-size:56px 56px;
  mask-image:linear-gradient(180deg, rgba(0,0,0,.55), transparent 78%);
  -webkit-mask-image:linear-gradient(180deg, rgba(0,0,0,.55), transparent 78%);
  pointer-events:none;
}
.career-hero__inner{
  position:relative;
  z-index:2;
  max-width:1180px;
  margin:0 auto;
  display:grid;
  grid-template-columns:1.15fr .85fr;
  gap:48px;
  align-items:center;
}
.career-hero__eyebrow{
  display:inline-block;
  padding:7px 16px;
  margin-bottom:18px;
  font-size:.74rem;
  font-weight:700;
  letter-spacing:.16em;
  text-transform:uppercase;
  color:#FFF4DE;
  background:rgba(230,143,0,.22);
  border:1px solid rgba(230,143,0,.55);
  border-radius:999px;
}
.career-hero__title{
  margin:0 0 14px;
  font-size:clamp(2.4rem, 5.4vw, 3.7rem);
  font-weight:800;
  line-height:1.06;
  letter-spacing:-.02em;
  color:#fff;
}
.career-hero__rule{
  display:block;
  width:78px; height:5px;
  border-radius:4px;
  margin-bottom:20px;
  background:linear-gradient(90deg,var(--cp-gold),#FFD27A);
}
.career-hero__tagline{
  margin:0 0 14px;
  font-size:clamp(1.05rem,2.1vw,1.35rem);
  font-weight:600;
  color:#FFE4C2;
}
.career-hero__lead{
  max-width:520px;
  margin:0 0 30px;
  font-size:1rem;
  color:rgba(255,255,255,.82);
}
.career-hero__actions{
  display:flex;
  flex-wrap:wrap;
  gap:14px;
}
.career-glass{
  padding:30px 28px;
  border-radius:22px;
  background:rgba(255,255,255,.10);
  border:1px solid rgba(255,255,255,.22);
  backdrop-filter:blur(10px);
  -webkit-backdrop-filter:blur(10px);
  box-shadow:0 24px 60px rgba(90,10,10,.35);
}
.career-glass h3{
  margin:0 0 18px;
  font-size:1.05rem;
  font-weight:700;
  letter-spacing:.02em;
  color:#fff;
}
.career-glass ul{ list-style:none; margin:0; padding:0; }
.career-glass li{
  position:relative;
  padding:11px 0 11px 30px;
  font-size:.95rem;
  color:rgba(255,255,255,.92);
  border-bottom:1px solid rgba(255,255,255,.14);
}
.career-glass li:last-child{ border-bottom:none; }
.career-glass li::before{
  content:"";
  position:absolute;
  left:4px; top:50%;
  width:9px; height:9px;
  margin-top:-4.5px;
  border-radius:50%;
  background:var(--cp-gold);
  box-shadow:0 0 0 4px rgba(230,143,0,.28);
}

/* ================= SECTIONS ================= */
.career-section{
  max-width:1180px;
  margin:0 auto;
  padding:64px 22px;
}
.career-section--tight{ padding-top:22px; }
.career-section__head{
  max-width:660px;
  margin:0 auto 32px;
  text-align:center;
}
.career-section__head h2{
  margin:12px 0 10px;
  font-size:clamp(1.6rem,3.2vw,2.15rem);
  font-weight:800;
  letter-spacing:-.01em;
  color:var(--cp-red-dark);
}
.career-section__head p{ margin:0; color:var(--cp-muted); }
.career-chip{
  display:inline-block;
  padding:6px 15px;
  font-size:.72rem;
  font-weight:700;
  letter-spacing:.15em;
  text-transform:uppercase;
  color:var(--cp-gold-dark);
  background:var(--cp-gold-soft);
  border:1px solid rgba(230,143,0,.35);
  border-radius:999px;
}

/* ================= INTRO ================= */
.career-intro{
  position:relative;
  padding:38px 40px 30px;
  background:var(--cp-white);
  border:1px solid var(--cp-border);
  border-radius:var(--cp-radius);
  box-shadow:var(--cp-shadow-sm);
}
.career-intro::before{
  content:"";
  position:absolute;
  left:0; top:26px; bottom:26px;
  width:5px;
  border-radius:0 6px 6px 0;
  background:linear-gradient(180deg,var(--cp-red),var(--cp-gold));
}
.career-intro p{ color:#4A3A36; font-size:1.02rem; }
.career-intro p:last-child{ margin-bottom:0; }
.career-intro strong{ color:var(--cp-red-dark); }
.career-intro a{
  color:var(--cp-red);
  font-weight:700;
  text-decoration:none;
  border-bottom:2px solid rgba(218,33,40,.28);
}
.career-intro a:hover{ border-bottom-color:var(--cp-red); }

/* ================= CARD & FORM ================= */
.career-card{
  background:var(--cp-white);
  border:1px solid var(--cp-border);
  border-radius:var(--cp-radius);
  box-shadow:var(--cp-shadow);
  padding:34px;
}
.career-grid{
  display:grid;
  grid-template-columns:repeat(2, minmax(0,1fr));
  gap:20px;
}
.career-field{ display:flex; flex-direction:column; min-width:0; }
.career-field--full{ grid-column:1 / -1; }
.career-field label{
  margin-bottom:8px;
  font-size:.86rem;
  font-weight:700;
  letter-spacing:.01em;
  color:var(--cp-red-dark);
}
.career-field label b{ color:var(--cp-red); }

.career-input,
.career-textarea,
.career-file{
  width:100%;
  padding:13px 15px;
  font-size:.95rem;
  font-family:inherit;
  color:var(--cp-text);
  background:#FFFDFA;
  border:1.5px solid var(--cp-border);
  border-radius:12px;
  transition:border-color .2s ease, box-shadow .2s ease, background .2s ease;
}
.career-textarea{ resize:vertical; min-height:110px; }
.career-input::placeholder,
.career-textarea::placeholder{ color:#B4A79E; }
.career-input:focus,
.career-textarea:focus,
.career-file:focus{
  outline:none;
  background:#fff;
  border-color:var(--cp-gold);
  box-shadow:0 0 0 4px rgba(230,143,0,.18);
}
.career-input.is-invalid,
.career-textarea.is-invalid,
.career-file.is-invalid{
  border-color:var(--cp-red);
  background:#FFF6F6;
}
.career-file{ padding:11px 13px; cursor:pointer; }
.career-file::file-selector-button{
  margin-right:14px;
  padding:9px 18px;
  font:inherit;
  font-size:.85rem;
  font-weight:700;
  color:#fff;
  background:var(--cp-btn-grad);
  border:none;
  border-radius:9px;
  cursor:pointer;
  transition:filter .2s ease;
}
.career-file::file-selector-button:hover{ filter:brightness(1.08); }

.career-error{
  margin:7px 0 0;
  font-size:.8rem;
  font-weight:600;
  color:var(--cp-red);
}
.career-hint{ margin-top:7px; font-size:.8rem; color:var(--cp-muted); }
.career-hint a{ font-weight:600; text-decoration:none; color:var(--cp-red); }

.career-recaptcha{ min-height:78px; display:flex; align-items:center; }

/* ================= BUTTONS ================= */
.career-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:9px;
  padding:13px 30px;
  font-family:inherit;
  font-size:.93rem;
  font-weight:700;
  letter-spacing:.02em;
  text-decoration:none;
  border:1.5px solid transparent;
  border-radius:999px;
  cursor:pointer;
  transition:transform .22s ease, box-shadow .22s ease, background .22s ease,
             color .22s ease, border-color .22s ease;
}
.career-btn:disabled{ opacity:.65; cursor:not-allowed; transform:none !important; }

/* Primary — uses #DC2928 → #E65228 */
.career-btn--primary{
  color:#fff;
  background:var(--cp-btn-grad);
  box-shadow:0 10px 24px rgba(220,41,40,.32);
}
.career-btn--primary:hover:not(:disabled){
  background:var(--cp-btn-grad-hover);
  transform:translateY(-2px);
  box-shadow:0 16px 32px rgba(220,41,40,.42);
}

/* Ghost — outlined on hero */
.career-btn--ghost{
  color:#fff;
  background:rgba(255,255,255,.06);
  border-color:rgba(255,255,255,.6);
}
.career-btn--ghost:hover{
  transform:translateY(-2px);
  background:rgba(230,143,0,.22);
  border-color:var(--cp-gold);
  color:#FFF4DE;
}

/* Muted / cancel */
.career-btn--muted{
  color:var(--cp-red-dark);
  background:var(--cp-cream-deep);
  border-color:#E0D3C4;
}
.career-btn--muted:hover{ background:#E8DDCB; }

.career-actions{
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  gap:18px;
  margin-top:26px;
  padding-top:24px;
  border-top:1px dashed var(--cp-border);
}
.career-actions .career-hint{ margin-top:0; }

/* ================= ALERTS ================= */
.career-alert{
  padding:13px 18px;
  margin-bottom:22px;
  font-size:.9rem;
  font-weight:600;
  border-radius:12px;
  border-left:4px solid transparent;
  animation:careerFade .35s ease both;
}
.career-alert--success{
  color:#12603F;
  background:#EAF8F1;
  border-left-color:var(--cp-success);
}
.career-alert--error{
  color:#8F1A1A;
  background:var(--cp-red-soft);
  border-left-color:var(--cp-red);
}
.career-alert--floating{ margin-top:22px; }

/* ================= INTERNSHIP ================= */
.career-intern{
  display:grid;
  grid-template-columns:.85fr 1.15fr;
  gap:0;
  overflow:hidden;
  border-radius:var(--cp-radius);
  background:var(--cp-white);
  border:1px solid var(--cp-border);
  box-shadow:var(--cp-shadow);
}
.career-intern__art{
  position:relative;
  min-height:260px;
  display:flex;
  align-items:flex-end;
  padding:30px;
  background:
    radial-gradient(500px 300px at 80% 0%, rgba(230,143,0,.55), transparent 62%),
    linear-gradient(150deg,#8E0F14 0%, #DA2128 100%);
}
.career-intern__art::before{
  content:"";
  position:absolute; inset:0;
  background-image:
    linear-gradient(rgba(255,255,255,.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.07) 1px, transparent 1px);
  background-size:44px 44px;
}
.career-intern__badge{
  position:relative;
  z-index:2;
  padding:9px 18px;
  font-size:.74rem;
  font-weight:700;
  letter-spacing:.16em;
  text-transform:uppercase;
  color:#FFF4DE;
  background:rgba(230,143,0,.24);
  border:1px solid rgba(230,143,0,.6);
  border-radius:999px;
}
.career-intern__body{ padding:40px 42px; }
.career-intern__body h2{
  margin:0 0 12px;
  font-size:clamp(1.5rem,2.9vw,2rem);
  font-weight:800;
  color:var(--cp-red-dark);
}
.career-intern__body p{
  max-width:560px;
  margin:0 0 26px;
  color:#4A3A36;
}
.career-rule{
  display:block;
  width:64px; height:4px;
  border-radius:4px;
  margin-bottom:18px;
  background:linear-gradient(90deg,var(--cp-red),var(--cp-gold));
}

/* ================= MODAL ================= */
.career-modal-backdrop{
  position:fixed;
  inset:0;
  z-index:1080;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:20px;
  background:rgba(50,10,10,.62);
  backdrop-filter:blur(4px);
  -webkit-backdrop-filter:blur(4px);
  animation:careerFade .22s ease both;
}
.career-modal{
  position:relative;
  width:100%;
  max-width:860px;
  max-height:92vh;
  overflow-y:auto;
  padding:34px 34px 30px;
  background:var(--cp-white);
  border-radius:20px;
  box-shadow:0 32px 80px rgba(80,10,10,.42);
  animation:careerPop .28s cubic-bezier(.2,.85,.35,1.05) both;
}
.career-modal__close{
  position:absolute;
  top:14px; right:16px;
  width:40px; height:40px;
  display:flex; align-items:center; justify-content:center;
  font-size:1.7rem;
  line-height:1;
  color:var(--cp-muted);
  background:var(--cp-cream);
  border:none;
  border-radius:50%;
  cursor:pointer;
  transition:background .2s ease, color .2s ease, transform .2s ease;
}
.career-modal__close:hover{
  color:#fff;
  background:var(--cp-red);
  transform:rotate(90deg);
}
.career-modal__title{
  margin:0 0 6px;
  font-size:1.5rem;
  font-weight:800;
  color:var(--cp-red-dark);
}
.career-modal__sub{
  margin:0 0 26px;
  color:var(--cp-muted);
  font-size:.92rem;
}

/* ================= ANIMATIONS ================= */
@keyframes careerFade{ from{ opacity:0; } to{ opacity:1; } }
@keyframes careerPop{
  from{ opacity:0; transform:translateY(24px) scale(.97); }
  to{ opacity:1; transform:translateY(0) scale(1); }
}

/* ================= RESPONSIVE ================= */
@media (max-width: 980px){
  .career-hero{ padding:58px 20px 70px; }
  .career-hero__inner{ grid-template-columns:1fr; gap:38px; }
  .career-intern{ grid-template-columns:1fr; }
  .career-intern__art{ min-height:150px; align-items:center; }
}
@media (max-width: 700px){
  .career-section{ padding:46px 18px; }
  .career-section--tight{ padding-top:8px; }
  .career-grid{ grid-template-columns:1fr; gap:16px; }
  .career-card{ padding:24px 20px; }
  .career-intro{ padding:28px 22px 22px; }
  .career-intro::before{ top:18px; bottom:18px; }
  .career-intern__body{ padding:28px 22px 32px; }
  .career-modal{ padding:26px 20px 24px; border-radius:16px; }
  .career-hero__actions .career-btn{ flex:1 1 auto; }
  .career-actions{ gap:12px; }
  .career-actions .career-btn{ flex:1 1 auto; }
  .career-recaptcha{ transform:scale(.94); transform-origin:left center; }
}
@media (max-width: 400px){
  .career-hero__title{ font-size:2.1rem; }
  .career-btn{ padding:12px 22px; font-size:.88rem; }
  .career-recaptcha{ transform:scale(.86); }
}
`;